import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Box,
  CircularProgress,
} from '@mui/material';
import { styled } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { motion, type Variants } from 'framer-motion';
import { Person, Apps, Campaign, Analytics, Lock } from '@mui/icons-material';
import useIdentities from 'hooks/useIdentities';
import { SPACING } from '../../../theme/theme';

const DashboardHeader = styled(Typography)`
  font-family: 'IRANYekanXFaNum';
  font-size: 18px;
  font-weight: 700;
  text-align: right;
  margin-bottom: 24px;
  color: #35373c;
`;

const BoxWrapper = styled(motion.div)<{
  bgcolor: string;
  bordercolor: string;
  locked?: boolean;
}>`
  background: ${({ bgcolor }) => bgcolor};
  /* 50% lighter than the source colour — mixed with white rather than using
     opacity, so the tile's own background stays fully saturated behind it. */
  border: 2px solid
    ${({ bordercolor }) => `color-mix(in srgb, ${bordercolor} 50%, #FFFFFF)`};
  border-radius: 16px;
  padding: 24px;
  cursor: ${({ locked }) => (locked ? 'not-allowed' : 'pointer')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
  opacity: ${({ locked }) => (locked ? 0.6 : 1)};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;

  /* Sheen that sweeps across on hover. Unlocked tiles only — a locked tile
     should look inert. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      120deg,
      transparent 30%,
      rgba(255, 255, 255, 0.45) 50%,
      transparent 70%
    );
    transform: translateX(-120%);
    transition: transform 0.35s ease;
    pointer-events: none;
  }

  &:hover::after {
    transform: ${({ locked }) => (locked ? 'translateX(-120%)' : 'translateX(120%)')};
  }

  /* Respect users who ask the OS to reduce motion. */
  @media (prefers-reduced-motion: reduce) {
    &::after {
      display: none;
    }
  }
`;

const BoxTitle = styled(Typography)`
  font-family: 'IRANYekanXFaNum';
  font-size: 15px;
  font-weight: 700;
  color: #35373c;
  text-align: center;
  margin-top: 12px;
`;

const IconContainer = styled(motion.div)`
  color: #35373c;
  font-size: 36px;
`;

/*
 * Entrance animation. The parent staggers its children, so each tile and its
 * icon arrive in sequence rather than every element firing on its own delay —
 * that keeps the timing correct if the tile list ever changes length.
 *
 * framer-motion reads `prefers-reduced-motion` when the app wraps it in
 * MotionConfig; the CSS sheen above opts out separately.
 */
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, delayChildren: 0 },
  },
};

const tileVariants: Variants = {
  hidden: { opacity: 0, y: 24, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 520, damping: 26 },
  },
  hover: {
    y: -8,
    scale: 1.03,
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.12)',
    transition: { type: 'spring', stiffness: 520, damping: 22 },
  },
};

/*
 * `visible` is the resting state; `hover` is driven by the tile's whileHover,
 * which propagates to children by variant name. Declaring both here keeps the
 * icon's hover motion out of the JSX.
 */
const iconVariants: Variants = {
  hidden: { opacity: 0, scale: 0.5, rotate: -12 },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: { type: 'spring', stiffness: 560, damping: 20 },
  },
  hover: {
    scale: 1.22,
    rotate: [0, -10, 8, 0],
    transition: {
      rotate: { duration: 0.45, ease: 'easeInOut' },
      scale: { type: 'spring', stiffness: 480, damping: 14 },
    },
  },
};

const MenuBox: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: identities, isLoading, isError } = useIdentities();
  const [publisherId, setPublisherId] = useState<string | null>(null);

  useEffect(() => {
    if (identities && identities.length > 0 && !publisherId) {
      const firstIdentity = identities[0];
      setPublisherId(firstIdentity.id.toString());
    }
  }, [identities, publisherId]);
  const items = [
    {
      title: 'هویت ها',
      path: '/identities',
      icon: <Person fontSize="large" />,
      bordercolor: '#FF9800', // نارنجی پررنگ
      bgcolor: 'rgba(255, 152, 0, 0.15)', // نارنجی با شفافیت کمتر
      locked: false,
    },
    {
      title: 'اپلیکیشن‌ها',
      path: publisherId ? `/applications?publisherId=${publisherId}` : '#',
      icon: <Apps fontSize="large" />,
      bordercolor: '#1976D2', // آبی پررنگ تر
      bgcolor: 'rgba(25, 118, 210, 0.15)', // آبی روشن با شفافیت
      locked: false,
    },
    {
      title: 'مدیریت کمپین و تبلیغات',
      path: '/campaigns',
      icon: <Campaign fontSize="large" />,
      bordercolor: '#7B1FA2', // بنفش پررنگ تر
      bgcolor: 'rgba(123, 31, 162, 0.15)', // بنفش روشن‌تر با شفافیت
      locked: true,
    },
    {
      title: 'آنالیتیک',
      path: '/analytics',
      icon: <Analytics fontSize="large" />,
      bordercolor: '#2E7D32', // سبز پررنگ تر
      bgcolor: 'rgba(46, 125, 50, 0.15)', // سبز روشن‌تر با شفافیت
      locked: true,
    },
  ];
  if (isLoading) {
    return <CircularProgress />;
  }

  if (isError || !identities || identities.length === 0) {
    return <Box>خطا در بارگذاری هویت‌ها یا هیچ هویتی موجود نیست</Box>;
  }
  return (
    // No maxWidth/padding here — SBox (AdminPanel/styles.ts) is the card and
    // already supplies both. Duplicating them just inset the grid twice.
    <motion.div
      style={{
        width: '100%',
        margin: '0 auto',
      }}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <DashboardHeader></DashboardHeader>

      {/* Pixel values — the theme sets spacing to 1, so the old `4` was a 4px
          gutter rather than the roomy gap it reads as. */}
      <Grid container spacing={isMobile ? SPACING.md : SPACING.lg}>
        {items.map((item, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <BoxWrapper
              bgcolor={item.bgcolor}
              bordercolor={item.bordercolor}
              locked={item.locked}
              variants={tileVariants}
              // Named variant, not an inline object — that is what lets the
              // hover state reach IconContainer's own `hover` variant.
              whileHover={item.locked ? undefined : 'hover'}
              whileTap={item.locked ? undefined : { scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 520, damping: 26 }}
              onClick={() => {
                if (!item.locked && item.path !== '#') navigate(item.path);
              }}
            >
              <IconContainer variants={iconVariants}>
                {item.locked ? <Lock fontSize="inherit" /> : item.icon}
              </IconContainer>
              <BoxTitle>{item.title}</BoxTitle>
            </BoxWrapper>
          </Grid>
        ))}
      </Grid>
    </motion.div>
  );
};

export default MenuBox;
